import React, { useEffect, useState } from "react";
import { CircleCheckBig, CircleUserRound, CircleX } from "lucide-react";
import { useParams } from "react-router-dom";

const Verify = () => {
    const { licenseNo } = useParams();
    const [surveyor, setSurveyor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const API_BASE = "https://portal.lsb.go.ke/account/verify";
    const MEDIA_ROOT = "https://portal.lsb.go.ke";

    // Dynamic year computation
    const currentYear = new Date().getFullYear();

    useEffect(() => {
        if (!licenseNo) return;

        setLoading(true);
        setError(null);

        fetch(`${API_BASE}/${licenseNo}/`)
            .then((res) => {
                if (!res.ok) throw new Error("Surveyor not found");
                return res.json();
            })
            .then((data) => {
                setSurveyor(data);
                setLoading(false);
            })
            .catch(() => {
                setError("Surveyor not found or license invalid.");
                setLoading(false);
            });
    }, [licenseNo]);

    // ✅ Loading state
    if (loading) {
        return (
            <div className="container mx-auto px-4 lg:px-8 py-28 flex flex-col justify-center items-center text-gray-600">
                <CircleUserRound className="w-16 h-16 animate-pulse mb-3" />
                <p>Verifying license number {licenseNo}...</p>
            </div>
        );
    }

    // ✅ Error / not found state
    if (error || !surveyor) {
        return (
            <div className="container mx-auto px-4 lg:px-8 py-28 flex flex-col justify-center items-center text-red-600">
                <CircleX className="w-20 h-20 mb-2" />
                <h2 className="text-2xl font-bold">Verification Failed</h2>
                <p className="text-center mt-2">
                    The license number <strong>{licenseNo}</strong> could not be verified.
                </p>
            </div>
        );
    }

    // ✅ Construct profile image URL dynamically
    const imagePath = surveyor.url || surveyor.profile_pic || "";
    let profilePicUrl = "";

    if (imagePath) {
        if (/^https?:\/\//i.test(imagePath)) {
            // Already a full URL
            profilePicUrl = imagePath;
        } else {
            // Relative media path — prepend server root
            profilePicUrl = `${MEDIA_ROOT}${imagePath.startsWith("/") ? "" : "/"}${imagePath}`;
        }
    } else {
        // Fallback: generate from initials
        profilePicUrl = `https://ui-avatars.com/api/?background=8d6b0b&color=fff&name=${encodeURIComponent(
            surveyor.name || "Surveyor"
        )}`;
    }

    // ✅ Display verified surveyor info
    return (
        <div className="container mx-auto px-4 lg:px-8 py-16 flex flex-col justify-center items-center leading-5 text-gray-800">
            <div className="relative w-full flex flex-col items-center">
                <img
                    src={profilePicUrl}
                    alt={surveyor.name || "Surveyor Photo"}
                    className="w-28 h-28 rounded-full object-cover border border-gray-300 shadow mt-2"
                    style={{ objectPosition: "top" }}
                />
            </div>

            <div className="flex flex-col justify-center items-center gap-3 pt-4">
                <CircleCheckBig className="w-8 h-8 text-green-600" />

                <h3 className="uppercase font-black underline underline-offset-4">
                    {surveyor.position || "Licensed Surveyor"}
                </h3>
            </div>

            <p className="pt-2">
                <b className="uppercase">Name:</b> {surveyor.name}
            </p>

            <p>
                <b className="uppercase">License Number:</b> {surveyor.license_no}
            </p>

            <p>
                <strong className="uppercase">Firm:</strong> {surveyor.firm || "N/A"}
            </p>

            {/* <p>
                <strong>Email:</strong> {surveyor.email || "N/A"}
            </p> */}

            <p>
                <strong className="uppercase">Address:</strong> {surveyor.postal_address || "N/A"}
            </p>

            <p>
                <strong>{currentYear} Status:</strong> {surveyor.status || "N/A"}{" "}
            </p>

            <p>
                <strong>Signature:</strong>{" "}
                <span className="underline underline-offset-4 text-green-500 font-bold">
                    {surveyor.hashed_no || "N/A"}
                </span>
            </p>
        </div>
    );
};

export default Verify;